import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'

import UserIcon from '@/assets/images/user_icon.svg'
import { Select } from '@/components/forms/Select'
import {
  AddIcon,
  DownloadIcon,
  EyeIcon,
  MinusIcon,
  PencilIcon,
  TrashIcon,
} from '@/components/icons'
import { CollapsibleSection } from '@/components/shared/CollapsibleSection'
import { DataTable } from '@/components/shared/DataTable'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import useAbortControllers from '@/hooks/AbortControllers'
import {
  deleteCreditMemo,
  getDealerBillingCycles,
  getDealerCreditMemos,
  getOutstandingDealerBillingCycles,
  updateBillingCycle,
} from '@/services/billing.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatCurrency, formatDate, formatDateTime, formatMonthName } from '@/utils/formatters'
import { handlePagination, toast } from '@/utils/helpers'

import ApprovalModal from './ApprovalModal'
import CreditMemoModal from './CreditMemoModal'
import ExpandedCreditMemoRow from './ExpandedCreditMemoRow'
import RejectionModal from './RejectionModal'
import SubmitForApprovalModal from './SubmitForApprovalModal'

// Icons

// Stores

// Utils & Service

const CreditMemos = ({ dealerId = '', fetchKpis }) => {
  // Context
  const { isWpcAdmin, isWpcExecutive, isReadOnlySession } = useContext(UserStoreContext)

  // State
  const [memos, setMemos] = useState([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [billingCycles, setBillingCycles] = useState([])
  const [outstandingBillingCycles, setOutstandingBillingCycles] = useState([])
  const [selectedBillingCycle, setSelectedBillingCycle] = useState({})
  const [selectedCreditMemo, setSelectedCreditMemo] = useState({})
  const [sortedColumn, setSortedColumn] = useState('date')

  const [loadingOutstandingBillingCycles, setLoadingOutstandingBillingCycles] = useState(true)
  const [loadingBillingCycle, setLoadingBillingCycle] = useState(true)
  const [loadingCreditMemos, setLoadingCreditMemos] = useState(true)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [loadingReopen, setLoadingReopen] = useState(false)

  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showSubmitForApprovalModal, setShowSubmitForApprovalModal] = useState(false)
  const [showReadOnlyModal, setShowReadOnlyModal] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [showZeroBillingModal, setShowZeroBillingModal] = useState(false)
  const [loadingZeroBilling, setLoadingZeroBilling] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState()
  const [perPage, setPerPage] = useState(20)
  const [pages, setPages] = useState(null)

  const MEMOS_BASE_URL = `/dealers/${dealerId}/billing-cycles/${selectedBillingCycle?.id}/credit-memos/?expand=created_by,add_backs,unbillables,files`

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  // Cancels superseded memo requests so the last billing cycle/page *requested*
  // wins, not the last to *resolve* (cycle-switch + pagination races).
  const { getSignal } = useAbortControllers()

  const canPerformActions =
    !(selectedBillingCycle?.state === 'Awaiting Approval' && isWpcAdmin) &&
    !(selectedBillingCycle?.state === 'Awaiting Executive Approval' && isWpcExecutive) &&
    !isReadOnlySession &&
    selectedBillingCycle?.state !== 'Submitted to Billing' &&
    selectedBillingCycle?.state !== 'Closed'

  /**
   * Gets the updated list of credit; updates pagination.
   * @param {string} url
   */
  const getUpdatedMemos = async (url, silent = false) => {
    let handleLoading = setLoadingCreditMemos
    if (silent) handleLoading = () => {}

    const response = await getDealerCreditMemos(
      url,
      handleLoading,
      handleErrors,
      () => {},
      getSignal('credit-memos'),
    )

    if (response) {
      setMemos(response.results)
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
    }
    return response
  }

  const fetchBillingCycles = async (updateSelectedCycle = false, silent = false) => {
    let handleLoading = setLoadingBillingCycle
    if (silent) handleLoading = () => {}

    const data = await getDealerBillingCycles(dealerId, handleLoading, handleErrors)

    if (data?.count > 0) {
      const updatedBillingCycles = _.map(data.results, (cycle) => ({
        ...cycle,
        id: cycle.id,
        label: `${formatMonthName(cycle?.period?.month)} ${cycle?.period?.year}`,
        year: cycle?.period?.year,
        month: cycle?.period?.month,
        state: cycle.state,
      }))

      setBillingCycles(updatedBillingCycles)

      if (updateSelectedCycle) {
        if (updatedBillingCycles.length > 0) {
          // Find the oldest non-submitted billing cycle
          const defaultCycle = _.findLast(
            updatedBillingCycles,
            (cycle) => cycle.state !== 'Submitted to Billing' && cycle.state !== 'Closed',
          )

          if (defaultCycle) {
            setSelectedBillingCycle(defaultCycle)
          } else {
            setSelectedBillingCycle(updatedBillingCycles[0]) // Fallback to the first in the list
          }
        }
      } else {
        setSelectedBillingCycle(
          updatedBillingCycles.find((c) => c.id === selectedBillingCycle.id) || {},
        )
      }
    } else {
      setBillingCycles([]) // Clear billing cycles if none are returned
      setSelectedBillingCycle({}) // Reset selectedBillingCycle if no billing cycles
      setMemos([]) // Clear memos since no billing cycles exist
      setSelectedBillingCycle({}) // Reset selected billing cycle
      setLoadingBillingCycle(false)
      setLoadingCreditMemos(false)
    }
  }

  const fetchOutstandingBillingCycles = async () => {
    const data = await getOutstandingDealerBillingCycles(
      dealerId,
      setLoadingOutstandingBillingCycles,
      handleErrors,
    )
    if (data?.count > 0) {
      setOutstandingBillingCycles(
        _.map(data.results, (cycle) => ({
          id: cycle.id,
          label: `${formatMonthName(cycle.period?.month)} ${cycle.period?.year}`,
          year: cycle.period?.year,
          month: cycle.period?.month,
          state: cycle.state,
        })),
      )
    } else {
      setOutstandingBillingCycles([]) // Clear billing cycles if none are returned
    }
  }

  useEffect(() => {
    setBillingCycles([])
    setSelectedBillingCycle({})
    setMemos([])

    if (dealerId) {
      setBillingCycles([]) // Clear previous billing cycles
      setSelectedBillingCycle({}) // Clear stale selected billing cycle
      setMemos([]) // Clear memos
      fetchBillingCycles(true) // Fetch billing cycles for the new dealer
      fetchOutstandingBillingCycles()
    } else {
      setBillingCycles([])
      setOutstandingBillingCycles([])
      setSelectedBillingCycle({})
      setMemos([])
    }
  }, [dealerId])

  useEffect(() => {
    if (selectedBillingCycle?.id) {
      getUpdatedMemos(
        `${MEMOS_BASE_URL}&limit=${perPage}&page=${currentPage}&order_by=${sortedColumn}`,
      )
    } else {
      setMemos([]) // Clear memos if no valid selectedBillingCycle
    }
  }, [selectedBillingCycle?.id, currentPage, perPage, sortedColumn])

  /**
   * Determines and returns the actions that are available based on `canPerformActions`.
   * @param {object} row
   * @returns
   */
  const renderActions = (row) => {
    if (canPerformActions) {
      return (
        <div>
          <button
            aria-label={`Edit Credit Memo ${row.memoId}`}
            onClick={() => {
              setSelectedCreditMemo(row)
              setShowCreditModal(true)
            }}
            className="inline-flex items-center rounded-lg p-2 text-center hover:bg-gray-100"
            type="button"
          >
            <PencilIcon className="h-4 stroke-white" />
          </button>
          <button
            aria-label={`Delete Credit Memo ${row.memoId}`}
            onClick={() => {
              setSelectedCreditMemo(row)
              setShowDeleteDialog(true)
            }}
            className="inline-flex items-center rounded-lg p-2 text-center hover:bg-gray-100"
            type="button"
          >
            <TrashIcon className="h-4 stroke-white" />
          </button>
        </div>
      )
    }

    return (
      <button
        aria-label={`View Credit Memo ${row.memoId}`}
        onClick={() => {
          setShowReadOnlyModal(true)
          setSelectedCreditMemo(row)
          setShowCreditModal(true)
        }}
        className="inline-flex items-center rounded-lg p-2 text-center hover:bg-gray-100"
        type="button"
      >
        <EyeIcon className="h-4 stroke-white" />
      </button>
    )
  }

  /**
   * Renders the added by user.
   * @param {object} row
   */
  const renderAddedBy = (row) => (
    <div className="flex flex-row gap-1">
      <img src={UserIcon} className="mr-1" alt="" />
      <span>{row.createdBy?.displayName || ''}</span>
    </div>
  )

  /**
   * Handles rendering any action buttons depending on the `status`.
   * @param {string} status
   * @param {number} selectedMonth
   */
  const renderActionButtons = (status, selectedMonth) => {
    if (!canPerformActions && !isWpcExecutive) {
      return null
    }

    switch (status) {
      case 'In Progress':
        return (
          <div className="m-auto flex gap-4 pt-8">
            <Button
              className="px-4 py-2"
              dataTestId="submitCreditMemos"
              label={`Submit ${dayjs()
                .month(selectedMonth - 1)
                .format('MMMM')} Memos for Approval`}
              onClick={() => setShowSubmitForApprovalModal(true)}
            />
          </div>
        )
      case 'Awaiting Executive Approval':
        return (
          <div className="m-auto flex gap-4 pt-8">
            <Button
              className="px-4 py-2"
              dataTestId="rejectCreditMemos"
              label="Reject"
              outlined
              background="bg-background"
              onClick={() => setShowRejectionModal(true)}
            />
            <Button
              className="px-3 py-2"
              dataTestId="approveCreditMemos"
              label="Approve"
              disabled={!isWpcExecutive}
              onClick={() => setShowApprovalModal(true)}
            />
          </div>
        )
      case 'Awaiting Approval':
        return (
          <div className="m-auto flex gap-4 pt-8">
            <Button
              className="px-4 py-2"
              dataTestId="rejectCreditMemos"
              label="Reject"
              outlined
              background="bg-background"
              onClick={() => setShowRejectionModal(true)}
            />
            <Button
              className="px-3 py-2"
              dataTestId="approveCreditMemos"
              label="Approve"
              onClick={() => setShowApprovalModal(true)}
            />
          </div>
        )

      case 'Submitted to Billing':
        return isWpcExecutive ? (
          <div className="m-auto pt-8">
            <Button
              className="px-4 py-2"
              dataTestId="reopenBillingMonth"
              label="Reopen Month"
              onClick={() => setShowReopenModal(true)}
              outlined
              background="bg-background"
            />
          </div>
        ) : null
      case 'Not Started':
        return (
          <div className="m-auto flex gap-4 pt-8">
            <Button
              className="px-4 py-2"
              dataTestId="submitZeroBilling"
              label={`No Billing for ${dayjs()
                .month(selectedMonth - 1)
                .format('MMMM')}`}
              outlined
              background="bg-background"
              disabled={loadingZeroBilling || loadingBillingCycle}
              onClick={() => setShowZeroBillingModal(true)}
            />
          </div>
        )
      case 'Closed':
      default:
        return null
    }
  }

  const columns = [
    {
      name: 'Memo Date',
      selector: (row) => formatDate(row.date),
      sortable: true,
      sortBy: 'date',
      minWidth: '150px',
    },
    {
      name: 'Credit Memo ID',
      selector: (row) => row.memoId,
      sortable: true,
      sortBy: 'memo_id',
      minWidth: '200px',
    },
    {
      name: 'Billing Total',
      selector: (row) => formatCurrency(row.totalAmount, false),
      sortable: true,
      sortBy: 'total_amount',
      minWidth: '150px',
    },
    {
      name: 'Memo Amount',
      selector: (row) => formatCurrency(row.memoAmount, false),
      sortable: true,
      sortBy: 'memo_amount',
      minWidth: '150px',
    },
    {
      name: 'Add Backs Total',
      selector: (row) => formatCurrency(row.totalAddBacks, false),
      sortable: true,
      sortBy: 'total_add_backs',
      minWidth: '150px',
    },
    {
      name: 'Unbillables',
      selector: (row) => formatCurrency(row.totalUnbillables, false),
      sortable: true,
      sortBy: 'total_unbillables',
      minWidth: '150px',
    },
    {
      name: 'Added By',
      cell: (row) => (row.createdBy && renderAddedBy(row)) || 'N/A',
      sortable: true,
      sortBy: 'created_by__first_name',
      minWidth: '250px',
    },
    {
      name: 'Actions',
      cell: renderActions,
      minWidth: '100px',
    },
  ]

  const hasAdminNotes = Boolean(selectedBillingCycle?.notesForAdmin)
  const showBillingNotes = Boolean(selectedBillingCycle?.notesForBilling) && isWpcExecutive
  const showNotes =
    hasAdminNotes ||
    showBillingNotes ||
    selectedBillingCycle?.pdfFile ||
    selectedBillingCycle?.cleanupPdfFile

  return (
    <div className="pt-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          {selectedBillingCycle?.id && (
            <Select
              className="relative w-full cursor-default rounded-md border border-none bg-transparent px-3 py-1 pl-0 text-left text-gray-900 shadow-none"
              inputTextClassName="text-lg"
              options={billingCycles}
              value={selectedBillingCycle}
              onChange={setSelectedBillingCycle}
              disabled={billingCycles.length === 0}
              showBadge
              dataTestId="billingCycleSelect"
            />
          )}
        </div>

        {canPerformActions && (
          <Button
            icon={<AddIcon className="h-4 stroke-white" />}
            label="Add Credit Memo"
            size="sm"
            dataTestId="addCreditMemo"
            disabled={!selectedBillingCycle?.id}
            onClick={() => {
              setShowCreditModal(true)
              setSelectedCreditMemo({})
            }}
          />
        )}
      </div>

      {showNotes && (
        <div className="my-4">
          <CollapsibleSection title="Billing and Approval Notes" defaultOpen>
            <div className="text-charcoal-900 flex flex-col gap-2 text-sm font-medium">
              {/*
                The PDFs live on cloud storage, so the `download` attribute is cross-origin and
                the browser ignores it. The link then replaced the portal in the same tab, and
                the user left WARP when they closed the PDF. Open a new tab instead.
              */}
              {selectedBillingCycle.pdfFile && (
                <a
                  href={selectedBillingCycle.pdfFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit items-center gap-2 text-sm font-medium text-blue-800 hover:underline"
                  aria-label="Download Billing PDF"
                >
                  <DownloadIcon className="h-4 stroke-blue-800" />
                  <span>Download Billing PDF</span>
                </a>
              )}

              {selectedBillingCycle.cleanupPdfFile && (
                <a
                  href={selectedBillingCycle.cleanupPdfFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit items-center gap-2 text-sm font-medium text-blue-800 hover:underline"
                  aria-label="Download Cleanup PDF"
                >
                  <DownloadIcon className="h-4 stroke-blue-800" />
                  <span>Download Cleanup PDF</span>
                </a>
              )}

              <div className="flex flex-col gap-1">
                {selectedBillingCycle.rejectedBy && (
                  <span className="font-semibold">
                    {selectedBillingCycle.rejectedBy.displayName} (
                    {formatDateTime(selectedBillingCycle.rejectedAt)}
                    ):
                  </span>
                )}
                <span>{selectedBillingCycle.notesForAdmin}</span>
              </div>

              {selectedBillingCycle.notesForBilling && (
                <div className="flex flex-col gap-1">
                  {selectedBillingCycle.submittedToBillingBy && (
                    <span className="font-semibold">
                      {selectedBillingCycle.submittedToBillingBy.displayName} (
                      {formatDateTime(selectedBillingCycle.submittedToBillingAt)}
                      ):
                    </span>
                  )}
                  <span>{selectedBillingCycle.notesForBilling}</span>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>
      )}

      <div className="mt-4">
        <DataTable
          columns={columns}
          data={memos}
          onSort={(column, direction) => {
            const d = direction === 'asc' ? '' : '-'
            setSortedColumn(`${d}${column.sortBy}`)
          }}
          progressPending={loadingCreditMemos || loadingBillingCycle}
          sortServer
          expandableRows
          expandableIcon={{
            collapsed: (
              <div className="flex flex-row gap-3 pl-3">
                <AddIcon className="h-4 stroke-blue-800" />
              </div>
            ),
            expanded: (
              <div className="flex flex-row gap-3 pl-4">
                <MinusIcon className="h-4 stroke-blue-800" />
              </div>
            ),
          }}
          expandableRowsComponent={ExpandedCreditMemoRow}
          pagination
          paginationServer
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[5, 10, 20, 30, 50]}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={(currentRowsPerPage) => setPerPage(currentRowsPerPage)}
          onChangePage={(page) => {
            handlePagination(
              page,
              currentPage,
              perPage,
              totalRows,
              pages,
              setCurrentPage,
              getUpdatedMemos,
              `${MEMOS_BASE_URL}&limit=`,
              `order_by=${sortedColumn}`,
            )
          }}
        />
      </div>

      <div className="m-auto flex">
        {renderActionButtons(selectedBillingCycle.state, selectedBillingCycle.month)}
      </div>

      {showDeleteDialog && selectedCreditMemo && (
        <Dialog
          title="Delete Credit Memo"
          type="warning"
          message={`Are you sure you want to delete this credit memo (ID: ${selectedCreditMemo.memoId})?`}
          onConfirm={() =>
            deleteCreditMemo(
              dealerId,
              selectedBillingCycle.id,
              selectedCreditMemo.id,
              setLoadingDelete,
              handleErrors,
              () => {
                setShowDeleteDialog(false)
                setSelectedCreditMemo({})

                getUpdatedMemos(
                  `${MEMOS_BASE_URL}&limit=${perPage}&page=${currentPage}&order_by=${sortedColumn}`,
                  true, // silent
                )
                fetchBillingCycles(
                  false, // updateSelectedCycle
                  true, // silent
                )
                fetchKpis(
                  true, // silent
                )
              },
            )
          }
          onCancel={() => setShowDeleteDialog(false)}
          loading={loadingDelete}
        />
      )}

      {showCreditModal && (
        <CreditMemoModal
          closeModal={() => {
            setShowCreditModal(false)
            setShowReadOnlyModal(false)
            setSelectedCreditMemo({})
          }}
          billingCycleId={selectedBillingCycle.id}
          dealerId={dealerId}
          billingCycles={outstandingBillingCycles}
          loadingBillingCycles={loadingOutstandingBillingCycles}
          creditMemo={selectedCreditMemo}
          readOnly={showReadOnlyModal}
          getUpdatedBillingCycle={() => {
            getUpdatedMemos(
              `${MEMOS_BASE_URL}&limit=${perPage}&page=${currentPage}&order_by=${sortedColumn}`,
              true, // silent
            )

            fetchBillingCycles(
              false, // updateSelectedCycle
              true, // silent
            )
            fetchKpis(
              true, // silent
            )
          }}
        />
      )}

      <RejectionModal
        showModal={showRejectionModal}
        setShowModal={setShowRejectionModal}
        billingCycleId={selectedBillingCycle?.id}
        dealerId={dealerId}
        getUpdatedBillingCycle={() => {
          fetchBillingCycles()
        }}
      />

      <SubmitForApprovalModal
        showModal={showSubmitForApprovalModal}
        setShowModal={setShowSubmitForApprovalModal}
        selectedBillingCycle={selectedBillingCycle}
        billingCycleId={selectedBillingCycle?.id}
        dealerId={dealerId}
        getUpdatedBillingCycle={() => {
          fetchBillingCycles()
        }}
      />

      <ApprovalModal
        showModal={showApprovalModal}
        setShowModal={setShowApprovalModal}
        selectedBillingCycle={selectedBillingCycle}
        billingCycleId={selectedBillingCycle?.id}
        dealerId={dealerId}
        getUpdatedBillingCycle={() => {
          fetchBillingCycles()
        }}
      />

      {showReopenModal && (
        <Dialog
          title="Reopen Billing Month"
          type="warning"
          message="Are you sure you want to reopen this billing month? An email will be sent to notify the Billing Department of this action."
          onConfirm={() =>
            updateBillingCycle(
              dealerId,
              selectedBillingCycle?.id,
              {
                markAsReopened: true,
              },
              setLoadingReopen,
              handleErrors,
              () => {
                handleSuccess('Billing cycle is reopened.')
                setShowReopenModal(false)
                fetchBillingCycles()
              },
            )
          }
          onCancel={() => setShowReopenModal(false)}
          loading={loadingReopen}
        />
      )}

      {showZeroBillingModal && (
        <Dialog
          title="Submit as No Billing"
          type="warning"
          message={`This will submit ${formatMonthName(
            selectedBillingCycle.month,
          )} as $0 for this dealer and notify the Billing Department. Continue?`}
          onConfirm={() =>
            updateBillingCycle(
              dealerId,
              selectedBillingCycle?.id,
              {
                markAsSubmittedZero: true,
              },
              setLoadingZeroBilling,
              handleErrors,
              () => {
                handleSuccess('Submitted as no billing for this month.')
                // Optimistically clear the Not Started state so the action
                // button hides immediately — fetchBillingCycles will reconcile.
                setSelectedBillingCycle((prev) => ({
                  ...prev,
                  state: 'Submitted to Billing',
                }))
                setShowZeroBillingModal(false)
                fetchBillingCycles()
                fetchKpis(true)
              },
            )
          }
          onCancel={() => setShowZeroBillingModal(false)}
          loading={loadingZeroBilling}
        />
      )}
    </div>
  )
}
CreditMemos.propTypes = {
  dealerId: PropTypes.string,
  fetchKpis: PropTypes.func.isRequired,
}

export default CreditMemos
